/**
 * The first word has right of way (job #773, speed red team #766 §1).
 *
 * The offline warm-up (`fillBuffer` in LearningPlayer.vue) used to start the
 * moment the first rounds existed: the whole-course walk, the listening
 * snapshot for every pod, and pod and head audio three at a time. All of that
 * shared a slow phone's pipe with the one clip the learner was waiting to
 * hear. Measured on Fast 3G: 1.7-3.3 s of the stranger's wait.
 *
 * This gate holds that warm-up until a lesson clip has actually SOUNDED, then
 * lets it run one fetch at a time until the first round the learner plays is
 * over, and only then at the concurrency the caller asked for.
 *
 * The warm-up still always happens: a request made while the gate is shut is
 * remembered and run the moment it opens. A learner who never presses play
 * still gets it, because the player opens the gate on a fallback timer once it
 * is ready.
 */
import { markFirstSound } from './firstSoundSignal'

export interface FirstSoundGate {
  /** True once a lesson clip has sounded, or the fallback fired. */
  readonly isOpen: boolean
  /** Open the gate. Idempotent. Runs the remembered request, if any. */
  open(reason: string): void
  /**
   * Ask to run `run` now. Returns false, and remembers the request, while
   * the gate is shut. Only the latest request is kept: a fill is idempotent.
   */
  admit(run: () => void): boolean
  /** Mark the first round the learner played as done. */
  markFirstRoundDone(): void
  /** The concurrency a fill may use right now. 1 until the first round is done. */
  concurrency(requested: number): number
  /** Why the gate opened, for the console and for tests. */
  readonly openedBy: string | null
}

export function createFirstSoundGate(): FirstSoundGate {
  let openedBy: string | null = null
  let firstRoundDone = false
  let pending: (() => void) | null = null
  return {
    get isOpen() { return openedBy !== null },
    get openedBy() { return openedBy },
    open(reason) {
      if (openedBy !== null) return
      openedBy = reason
      markFirstSound(reason) // releases the app's other background walks (job #828)
      const run = pending
      pending = null
      run?.()
    },
    admit(run) {
      if (openedBy !== null) return true
      pending = run
      return false
    },
    markFirstRoundDone() { firstRoundDone = true },
    concurrency(requested) {
      return firstRoundDone ? Math.max(1, requested) : 1
    },
  }
}

/**
 * How long after the player is ready the gate opens anyway, if no lesson
 * clip has sounded by then. A learner who opens the app and does not press
 * play is not waiting for a first word, so the warm-up may use the pipe.
 * Before job #773 that learner got the warm-up at once; this keeps it.
 */
export const FIRST_SOUND_FALLBACK_MS = 20_000
